import React, { useMemo, useState } from "react";
import { getSegmentGuide } from "../data/segmentGuideMock";
import {
  getCodeSetOptions,
  getEdiPreviewTokens,
  resolveElementLengths,
  resolveElementSampleValue,
  validateClientConfigSampleLength,
  validateSampleLength,
} from "../data/ediHelpers";
import OptionalSegmentVisibility from "./OptionalSegmentVisibility";

const SourceBadge = ({ sourceType }) => {
  const labels = {
    dynamic: "Dynamic (FHIR)",
    static: "Static",
    system: "System Generated",
  };
  return (
    <span className={`explorer-source-badge ${sourceType}`}>
      {labels[sourceType] ?? sourceType}
    </span>
  );
};

const DynamicSourcePathInput = ({
  elementId,
  basePath,
  value,
  onChange,
}) => (
  <input
    type="text"
    className="explorer-source-path-input"
    value={value}
    onChange={(e) => onChange(elementId, e.target.value, basePath)}
    placeholder={basePath || "FHIR path"}
    aria-label={`FHIR path for ${elementId}`}
    spellCheck={false}
  />
);

const isClientConfigEl = (el) =>
  el?.source === "clientConfig" ||
  el?.source === "client" ||
  (typeof el?.binding === "string" && el.binding.startsWith("Envelope."));

const isSampleEditable = (el) =>
  el.sourceType === "dynamic" || isClientConfigEl(el);

const SampleValueCell = ({
  element,
  sample,
  options,
  segmentTag,
  onChange,
}) => {
  const { minLength, maxLength } = resolveElementLengths(element, segmentTag);
  const lengthError = isClientConfigEl(element)
    ? validateClientConfigSampleLength(sample, minLength, maxLength)
    : validateSampleLength(sample, minLength, maxLength);

  if (options.length > 0) {
    const inList = options.some((opt) => opt.code === sample);
    return (
      <div className="explorer-sample-field">
        <select
          className={`explorer-sample-select${lengthError ? " is-invalid" : ""}`}
          value={sample}
          onChange={(e) => onChange(element.elementId, e.target.value, element)}
          aria-label={`Sample value for ${element.elementId}`}
          aria-invalid={!!lengthError}
        >
          {!inList && sample !== "" && (
            <option value={sample}>{sample}</option>
          )}
          {options.map((opt) => (
            <option key={`${opt.code}|${opt.description}`} value={opt.code}>
              {opt.description ? `${opt.code} — ${opt.description}` : opt.code}
            </option>
          ))}
        </select>
        {lengthError && (
          <div className="explorer-sample-error" role="alert">
            {lengthError}
          </div>
        )}
      </div>
    );
  }

  if (isSampleEditable(element)) {
    return (
      <div className="explorer-sample-field">
        <input
          type="text"
          className={`explorer-sample-input${lengthError ? " is-invalid" : ""}`}
          value={sample}
          onChange={(e) => onChange(element.elementId, e.target.value, element)}
          aria-label={`Sample value for ${element.elementId}`}
          aria-invalid={!!lengthError}
          spellCheck={false}
        />
        {lengthError && (
          <div className="explorer-sample-error" role="alert">
            {lengthError}
          </div>
        )}
      </div>
    );
  }

  return <code className="explorer-sample-val">{sample}</code>;
};

const EdiPreview = ({ segment }) => {
  const tokens = getEdiPreviewTokens(segment);

  return (
    <pre className="explorer-edi-preview">
      <code>
        {tokens.map((token, idx) => {
          if (token.type === "tag") {
            return (
              <span key={idx} className="edi-token-tag">
                {token.text}
              </span>
            );
          }
          if (token.type === "delimiter") {
            return (
              <span key={idx} className="edi-token-delimiter">
                {token.text}
              </span>
            );
          }
          if (token.type === "empty" || token.type === "empty-field") {
            if (token.text && !token.text.includes("*")) {
              return (
                <span key={idx} className="edi-token-empty-msg">
                  {token.text}
                </span>
              );
            }
            return null;
          }
          if (token.type === "placeholder") {
            return (
              <span key={idx} className="edi-token-placeholder">
                {token.text}
              </span>
            );
          }
          return (
            <span key={idx} className={`edi-token-value ${token.type}`}>
              {token.text}
            </span>
          );
        })}
      </code>
    </pre>
  );
};

const resolveDynamicPathBase = (el) => {
  const path = el?.fhirPath ?? el?.sourceDisplay ?? "";
  if (!path || path === "—") return "";
  // Never show internal model bindings in the FHIR path input.
  if (el?.binding && String(path).trim() === String(el.binding).trim()) {
    return "";
  }
  if (/^[A-Z][A-Za-z0-9]*(\.[A-Z][A-Za-z0-9]*)+$/.test(String(path).trim())) {
    const fhirRoots = [
      "Claim",
      "Coverage",
      "Patient",
      "Practitioner",
      "Organization",
      "Location",
      "DocumentReference",
      "Bundle",
    ];
    const root = String(path).split(".")[0];
    if (!fhirRoots.includes(root)) return "";
  }
  return path;
};

const MappingExplorerPanel = ({
  segment,
  onToggleSegmentVisibility,
  codeSets,
  clientConfigSamples = {},
  onClientConfigSampleChange,
  pathEdits = {},
  onFhirPathChange,
}) => {
  const [sampleEdits, setSampleEdits] = useState({});

  const handleFhirPathChange = (elementId, fhirPath, baseline = "") => {
    if (!onFhirPathChange) return;
    onFhirPathChange(elementId, fhirPath, baseline);
  };

  const handleSampleChange = (elementId, value, element) => {
    if (isClientConfigEl(element) && onClientConfigSampleChange) {
      onClientConfigSampleChange(elementId, value);
      return;
    }
    setSampleEdits((prev) => ({ ...prev, [elementId]: value }));
  };

  const previewSegment = useMemo(() => {
    if (!segment) return null;
    return {
      ...segment,
      elements: (segment.elements || []).map((el) => {
        if (isClientConfigEl(el)) {
          if (
            Object.prototype.hasOwnProperty.call(
              clientConfigSamples,
              el.elementId
            )
          ) {
            return { ...el, value: clientConfigSamples[el.elementId] };
          }
          return el;
        }
        if (!Object.prototype.hasOwnProperty.call(sampleEdits, el.elementId)) {
          return el;
        }
        return { ...el, value: sampleEdits[el.elementId] };
      }),
    };
  }, [segment, sampleEdits, clientConfigSamples]);

  if (!segment) {
    return (
      <div className="explorer-panel explorer-panel-empty">
        <div className="explorer-empty-icon">278</div>
        <h2>FHIR → X12 Mapping Explorer</h2>
        <p>
          Select a segment from the structure tree to view its FHIR mapping and
          generated EDI preview for this Prior Authorization request.
        </p>
      </div>
    );
  }

  const guide = getSegmentGuide(segment.segmentId);
  const segmentName = segment.friendlyName ?? segment.segmentId;

  return (
    <div className="explorer-panel">
      <header className="explorer-panel-header">
        <div className="explorer-panel-segment-id">
          <span className="explorer-tag-lg">{segment.tag}</span>
          <span className="explorer-segment-id-text">{segment.segmentId}</span>
        </div>
        <h2 className="explorer-panel-title">{segmentName}</h2>
        <p className="explorer-panel-loop">
          {segment.loopName}
          {segment.pos && ` · POS ${segment.pos}`}
        </p>
      </header>

      <section className="explorer-section">
        <h3 className="explorer-section-title">Segment Information</h3>
        <dl className="explorer-info-grid">
          <div className="explorer-info-item">
            <dt>Segment ID</dt>
            <dd>
              <code>{segment.tag}</code>
            </dd>
          </div>
          <div className="explorer-info-item">
            <dt>Name</dt>
            <dd>{segmentName}</dd>
          </div>
          <div className="explorer-info-item full">
            <dt>Description</dt>
            <dd>{guide.description}</dd>
          </div>
          <div className="explorer-info-item full">
            <dt>Purpose</dt>
            <dd>{guide.purpose}</dd>
          </div>
          <div className="explorer-info-item">
            <dt>Usage</dt>
            <dd>
              <span
                className={`explorer-usage-badge ${
                  segment.isOptional ? "optional" : "required"
                }`}
              >
                {segment.isOptional ? "Optional" : "Required"}
              </span>
            </dd>
          </div>
          {segment.isOptional && onToggleSegmentVisibility && (
            <div className="explorer-info-item full">
              <dt>Generation</dt>
              <dd className="explorer-generation-control">
                <OptionalSegmentVisibility
                  visible={segment.isUserVisible}
                  segmentId={segment.segmentId}
                  onChange={onToggleSegmentVisibility}
                  compact={false}
                />
                <span className="explorer-generation-hint">
                  {segment.isUserVisible
                    ? "This segment will be included in the generated 278."
                    : "This segment will be omitted from the generated 278."}
                </span>
              </dd>
            </div>
          )}
        </dl>
      </section>

      <section className="explorer-section">
        <div className="explorer-section-head">
          <h3 className="explorer-section-title">FHIR Mapping</h3>
          <div className="explorer-legend">
            <span className="explorer-source-badge dynamic">Dynamic (FHIR)</span>
            <span className="explorer-source-badge static">Static</span>
            <span className="explorer-source-badge system">System Generated</span>
          </div>
        </div>
        <div className="explorer-table-wrap">
          <table className="explorer-mapping-table">
            <thead>
              <tr>
                <th>Element</th>
                <th>Description</th>
                <th>Source</th>
                <th>Sample Value</th>
              </tr>
            </thead>
            <tbody>
              {segment.elements.map((el) => {
                const baseSample = resolveElementSampleValue(
                  el,
                  segment.segmentId,
                  segment.tag
                );
                let sample = baseSample;
                if (isClientConfigEl(el)) {
                  if (
                    Object.prototype.hasOwnProperty.call(
                      clientConfigSamples,
                      el.elementId
                    )
                  ) {
                    sample = clientConfigSamples[el.elementId];
                  }
                } else if (
                  Object.prototype.hasOwnProperty.call(sampleEdits, el.elementId)
                ) {
                  sample = sampleEdits[el.elementId];
                }
                const options = getCodeSetOptions(codeSets, el.dropdownSource);
                return (
                  <tr key={el.elementId}>
                    <td>
                      <code className="explorer-el-code">{el.elementId}</code>
                    </td>
                    <td>{el.friendlyLabel ?? el.label}</td>
                    <td>
                      <div className="explorer-source-cell">
                        <SourceBadge sourceType={el.sourceType} />
                        {el.sourceType === "dynamic" ? (
                          <DynamicSourcePathInput
                            elementId={el.elementId}
                            basePath={resolveDynamicPathBase(el)}
                            value={
                              pathEdits[el.elementId] ??
                              resolveDynamicPathBase(el)
                            }
                            onChange={handleFhirPathChange}
                          />
                        ) : (
                          <span className="explorer-source-path">
                            {el.fhirPath || el.sourceDisplay}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <SampleValueCell
                        element={el}
                        sample={sample}
                        options={options}
                        segmentTag={segment.tag}
                        onChange={handleSampleChange}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="explorer-section">
        <h3 className="explorer-section-title">Generated EDI Preview</h3>
        <p className="explorer-section-desc">
          Segment output before the Prior Authorization request is sent to the payer.
        </p>
        <EdiPreview segment={previewSegment} />
        <div className="explorer-preview-legend">
          <span className="edi-legend-item">
            <span className="edi-legend-swatch dynamic" /> Dynamic (FHIR)
          </span>
          <span className="edi-legend-item">
            <span className="edi-legend-swatch static" /> Static
          </span>
          <span className="edi-legend-item">
            <span className="edi-legend-swatch system" /> System Generated
          </span>
        </div>
      </section>
    </div>
  );
};

export default MappingExplorerPanel;
